// Organizasyon kalemleri düzenleyicisi — teklif formu ve Ayarlar > Organizasyon (paket/hazır kalem) ortak kullanır.
//
// items: [{ key, name, description, quantity, unit, unitPrice, vatRate }]  (sayılar HAM metin: "1.500", "2,5", "10")
// Toplam = adet × birim fiyat (utils/eventPricing.js). Sayı alanları type="text": "1.500" Türkçe biçimde okunur.
// KDV oranı kalem başına seçilir (yemek %10, salon/hizmet %20 …); yeni satır defaultVatRate ile gelir.
import React from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Copy, PackagePlus, ListPlus } from 'lucide-react';
import { eventLineTotal, toEventNumber, UNIT_SUGGESTIONS, VAT_RATE_OPTIONS } from '../../utils/eventPricing';
import { formatMoneyTR } from '../../utils/pricing';
import { emptyItem, itemFromSaved, newItemKey, hasTooManyDecimals } from './eventApi';

const inputCls = 'w-full p-2 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400';
const fmtRate = (n) => Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

// Seçenekler: standart oranlar + (varsa) satırın/varsayılanın standart dışı oranı — kayıtlı değer kaybolmasın
const rateOptions = (current, defaultRate) => {
  const extra = [current, defaultRate].map(toEventNumber).filter(n => Number.isFinite(n) && n >= 0 && n <= 100);
  return [...new Set([...VAT_RATE_OPTIONS, ...extra])].sort((a, b) => b - a);
};

export default function EventItemsEditor({
  items, onChange, currency = 'TL', library = [], packages = [], readOnly = false, title = 'Kalemler', defaultVatRate = 20,
}) {
  const update = (key, field, value) => onChange(items.map(it => (it.key === key ? { ...it, [field]: value } : it)));
  const remove = (key) => onChange(items.filter(it => it.key !== key));
  const duplicate = (key) => {
    const i = items.findIndex(it => it.key === key);
    const copy = { ...items[i], key: newItemKey() };
    onChange([...items.slice(0, i + 1), copy, ...items.slice(i + 1)]);
  };
  const move = (key, dir) => {
    const i = items.findIndex(it => it.key === key);
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  // Tek satır boş ve isimsizse eklenenle değiştirilir (formun ilk boş satırı kalmasın)
  const append = (newItems) => {
    const base = items.length === 1 && !String(items[0].name || '').trim() && !String(items[0].unitPrice || '').trim() ? [] : items;
    onChange([...base, ...newItems]);
  };

  // Kütüphane fiyatları TL'dir (Ayarlar'da TL girilir): döviz teklifinde kur çevrilmeden eklenmesin
  const confirmCurrency = () => currency === 'TL' || window.confirm(
    `Hazır kalem ve paket fiyatları TL olarak kayıtlıdır; bu teklif ${currency}.\n\n` +
    `Eklenen satırların birim fiyatları ${currency} karşılığına göre elle güncellenmelidir. Yine de eklensin mi?`
  );
  const addFromLibrary = (id) => {
    const entry = library.find(l => String(l.id) === String(id));
    if (entry && confirmCurrency()) append([itemFromSaved(entry, defaultVatRate)]);
  };
  const addPackage = (id) => {
    const pkg = packages.find(p => String(p.id) === String(id));
    if (pkg?.items?.length && confirmCurrency()) append(pkg.items.map(it => itemFromSaved(it, defaultVatRate)));
  };

  const total = items.reduce((acc, it) => acc + eventLineTotal(it), 0);

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2">
            {library.length > 0 && (
              <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                <ListPlus size={16} className="shrink-0" />
                <span className="sr-only">Hazır kalem ekle</span>
                <select className="p-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" value=""
                  onChange={e => { addFromLibrary(e.target.value); e.target.value = ''; }}>
                  <option value="">Hazır kalem ekle…</option>
                  {library.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </label>
            )}
            {packages.length > 0 && (
              <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                <PackagePlus size={16} className="shrink-0" />
                <span className="sr-only">Paket ekle</span>
                <select className="p-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" value=""
                  onChange={e => { addPackage(e.target.value); e.target.value = ''; }}>
                  <option value="">Paket ekle…</option>
                  {packages.map(p => <option key={p.id} value={p.id}>{p.name} ({p.items?.length || 0} kalem)</option>)}
                </select>
              </label>
            )}
            <button type="button" onClick={() => onChange([...items, emptyItem(defaultVatRate)])}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center gap-1 text-sm font-medium">
              <Plus size={16} /> Satır Ekle
            </button>
          </div>
        )}
      </div>

      <datalist id="event-unit-options">
        {UNIT_SUGGESTIONS.map(u => <option key={u} value={u} />)}
      </datalist>

      {items.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 p-4 border border-dashed rounded-lg text-center">
          Henüz kalem yok. "Satır Ekle" ile başlayın.
        </p>
      )}

      <div className="space-y-3">
        {items.map((it, index) => {
          const q = toEventNumber(it.quantity);
          const p = toEventNumber(it.unitPrice);
          const qBad = String(it.quantity ?? '').trim() !== '' && (!Number.isFinite(q) || q <= 0 || hasTooManyDecimals(q));
          const pBad = String(it.unitPrice ?? '').trim() !== '' && (!Number.isFinite(p) || p < 0 || hasTooManyDecimals(p));
          const vatNum = toEventNumber(it.vatRate);
          const vatBad = !Number.isFinite(vatNum) || vatNum < 0 || vatNum > 100;
          return (
            <div key={it.key} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/60">
              <div className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-12 xl:col-span-4">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{index + 1}. Kalem adı</label>
                  <input type="text" className={inputCls} value={it.name} maxLength={255} disabled={readOnly}
                    onChange={e => update(it.key, 'name', e.target.value)} placeholder="ör. Akşam Yemeği Menüsü" />
                </div>
                <div className="col-span-4 md:col-span-3 xl:col-span-2">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Adet</label>
                  <input type="text" inputMode="decimal" autoComplete="off" disabled={readOnly}
                    className={`${inputCls} text-right ${qBad ? 'border-red-400 dark:border-red-500' : ''}`}
                    value={it.quantity} onChange={e => update(it.key, 'quantity', e.target.value)} placeholder="1" />
                </div>
                <div className="col-span-4 md:col-span-2 xl:col-span-1">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Birim</label>
                  <input type="text" list="event-unit-options" maxLength={30} className={inputCls} value={it.unit} disabled={readOnly}
                    onChange={e => update(it.key, 'unit', e.target.value)} placeholder="adet" />
                </div>
                <div className="col-span-4 md:col-span-3 xl:col-span-2">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Birim fiyat ({currency})</label>
                  <input type="text" inputMode="decimal" autoComplete="off" disabled={readOnly}
                    className={`${inputCls} text-right ${pBad ? 'border-red-400 dark:border-red-500' : ''}`}
                    value={it.unitPrice} onChange={e => update(it.key, 'unitPrice', e.target.value)} placeholder="ör. 1.500" />
                </div>
                <div className="col-span-4 md:col-span-2 xl:col-span-1">
                  <label htmlFor={`vat-${it.key}`} className="block text-xs text-gray-500 dark:text-gray-400 mb-1">KDV</label>
                  <select id={`vat-${it.key}`} disabled={readOnly}
                    className={`${inputCls} px-1 ${vatBad ? 'border-red-400 dark:border-red-500' : ''}`}
                    value={vatBad ? '' : String(vatNum)} onChange={e => update(it.key, 'vatRate', e.target.value)}>
                    {vatBad && <option value="">Seçin</option>}
                    {rateOptions(it.vatRate, defaultVatRate).map(r => <option key={r} value={String(r)}>%{fmtRate(r)}</option>)}
                  </select>
                </div>
                <div className="col-span-8 md:col-span-2">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Toplam</label>
                  <div className="p-2 text-right font-bold text-gray-800 dark:text-gray-100 text-sm">
                    {formatMoneyTR(eventLineTotal(it))} {currency}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-10">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Özellikler / açıklama (PDF'te kalem adının altında görünür)</label>
                  <textarea rows={2} className={inputCls} value={it.description} maxLength={5000} disabled={readOnly}
                    onChange={e => update(it.key, 'description', e.target.value)}
                    placeholder="ör. Çorba, ara sıcak, ana yemek, tatlı, meşrubat" />
                </div>
                {!readOnly && (
                  <div className="col-span-12 md:col-span-2 flex md:flex-col md:items-end justify-end gap-1 md:pt-5">
                    <div className="flex gap-1">
                      <button type="button" title="Yukarı taşı" aria-label={`${index + 1}. kalemi yukarı taşı`} disabled={index === 0}
                        onClick={() => move(it.key, -1)} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30">
                        <ChevronUp size={16} />
                      </button>
                      <button type="button" title="Aşağı taşı" aria-label={`${index + 1}. kalemi aşağı taşı`} disabled={index === items.length - 1}
                        onClick={() => move(it.key, 1)} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30">
                        <ChevronDown size={16} />
                      </button>
                      <button type="button" title="Satırı kopyala" aria-label={`${index + 1}. kalemi kopyala`}
                        onClick={() => duplicate(it.key)} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
                        <Copy size={16} />
                      </button>
                      <button type="button" title="Satırı sil" aria-label={`${index + 1}. kalemi sil`}
                        onClick={() => remove(it.key)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {items.length > 0 && (
        <p className="mt-3 text-right text-sm text-gray-600 dark:text-gray-300">
          Kalemler toplamı: <strong className="text-gray-900 dark:text-white">{formatMoneyTR(total)} {currency}</strong>
        </p>
      )}
    </div>
  );
}
